import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ScrollReveal, { ScrollStaggerContainer } from '@/components/base/ScrollReveal';

export default function Process() {
  const { t } = useTranslation();

  const steps = [
    { number: '01', titleKey: 'process.step1', descKey: 'process.step1Desc', icon: 'ri-search-eye-line' },
    { number: '02', titleKey: 'process.step2', descKey: 'process.step2Desc', icon: 'ri-lightbulb-line' },
    { number: '03', titleKey: 'process.step3', descKey: 'process.step3Desc', icon: 'ri-palette-line' },
    { number: '04', titleKey: 'process.step4', descKey: 'process.step4Desc', icon: 'ri-code-s-slash-line' },
    { number: '05', titleKey: 'process.step5', descKey: 'process.step5Desc', icon: 'ri-rocket-2-line' },
    { number: '06', titleKey: 'process.step6', descKey: 'process.step6Desc', icon: 'ri-line-chart-line' },
    { number: '07', titleKey: 'process.step7', descKey: 'process.step7Desc', icon: 'ri-bar-chart-2-line' },
  ];

  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="text-center mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('process.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('process.title')}{' '}
              <span className="text-primary-500">{t('process.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
              {t('process.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <div className="relative">
          {/* Vertical line with scroll animation */}
          <motion.div
            className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-px origin-top"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          ></motion.div>

          <ScrollStaggerContainer className="space-y-6 md:space-y-0" staggerDelay={0.15} delay={0.3}>
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`relative flex flex-col md:flex-row items-center gap-6 md:gap-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } md:mb-12`}
              >
                {/* Timeline dot */}
                <motion.div
                  className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-primary-500 text-white items-center justify-center z-10"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.15, duration: 0.5, type: 'spring' }}
                >
                  <span className="font-heading text-lg font-bold">{step.number}</span>
                </motion.div>

                <div className={`w-full md:w-[calc(50%-50px)] p-6 md:p-7 rounded-2xl bg-gray-50 border border-gray-200 hover:border-red-300/50 transition-all duration-300 group ${
                  index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                }`}>
                  <div className="md:hidden w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center mb-3">
                    <span className="font-heading text-sm font-bold">{step.number}</span>
                  </div>
                  <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-600 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-300">
                      <i className={`${step.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{t(step.titleKey)}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{t(step.descKey)}</p>
                </div>
              </div>
            ))}
          </ScrollStaggerContainer>
        </div>
      </div>
    </section>
  );
}